import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, Send } from "lucide-react";
import { Button } from "./ui/button";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [allowDisplay, setAllowDisplay] = useState(false);
  const [step, setStep] = useState(1); // 1: rating, 2: text (for good ratings)
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if we should ask for review
    const checkReview = async () => {
      try {
        const res = await apiClient.get("/support/reviews/should-ask");
        if (res.data.should_ask) {
          // Delay showing the prompt by 2 seconds after page load
          setTimeout(() => setShowPrompt(true), 2000);
        }
      } catch (error) {
        // Silently fail - don't bother user if check fails
      }
    };

    checkReview();
  }, []);

  const handleRatingClick = async (selectedRating) => {
    setRating(selectedRating);
    
    if (selectedRating >= 4) {
      // Good rating - ask for text review
      setStep(2);
    } else {
      // Bad rating - submit immediately and close
      await submitReview(selectedRating, "", false);
    }
  };

  const submitReview = async (finalRating, text, display) => {
    setLoading(true);
    try {
      await apiClient.post("/support/reviews", {
        rating: finalRating || rating,
        review_text: text || reviewText,
        allow_display: display || allowDisplay
      });
      
      if ((finalRating || rating) >= 4) {
        toast.success("Thank you for your review! 🎉");
      } else {
        toast.success("Thanks for your feedback. We'll work to improve!");
      }
      
      setShowPrompt(false);
    } catch (error) {
      toast.error("Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={() => setShowPrompt(false)}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-viraly-card rounded-2xl p-6 max-w-md w-full shadow-2xl border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold">How's your experience?</h3>
            <button
              onClick={() => setShowPrompt(false)}
              className="text-viraly-muted hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {step === 1 && (
            <>
              <p className="text-viraly-muted mb-6">
                We'd love to hear your feedback! Tap a star to rate Viraly.
              </p>

              <div className="flex justify-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleRatingClick(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="transition-transform hover:scale-110"
                    data-testid={`star-${star}`}
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        star <= (hoveredRating || rating)
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-500"
                      }`}
                    />
                  </button>
                ))}
              </div>

              <p className="text-center text-sm text-viraly-muted">
                {hoveredRating === 1 && "Poor"}
                {hoveredRating === 2 && "Fair"}
                {hoveredRating === 3 && "Good"}
                {hoveredRating === 4 && "Very Good"}
                {hoveredRating === 5 && "Excellent!"}
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <div className="flex justify-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-6 h-6 ${
                      star <= rating
                        ? "text-yellow-400 fill-yellow-400"
                        : "text-gray-500"
                    }`}
                  />
                ))}
              </div>

              <p className="text-viraly-muted mb-4 text-center">
                {rating === 5 ? "Awesome! 🎉" : "Great!"} Would you like to share more?
              </p>

              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Tell us what you love about Viraly..."
                className="w-full bg-viraly-dark border border-white/20 rounded-xl p-3 text-sm resize-none h-24 focus:border-viraly-orange focus:outline-none mb-4"
                data-testid="review-text"
              />

              <label className="flex items-center gap-3 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowDisplay}
                  onChange={(e) => setAllowDisplay(e.target.checked)}
                  className="w-4 h-4 rounded accent-viraly-orange"
                />
                <span className="text-sm text-viraly-muted">
                  Allow Viraly to display my review publicly
                </span>
              </label>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={() => submitReview()}
                  disabled={loading}
                  className="flex-1 btn-primary"
                  data-testid="submit-review-btn"
                >
                  {loading ? (
                    <span className="animate-spin">⏳</span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
